import {
  Component,
  input,
  output,
  signal,
  inject,
  OnInit,
  computed
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { DialogModule } from 'primeng/dialog';
import { TableModule } from 'primeng/table';
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { DropdownModule } from 'primeng/dropdown';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TooltipModule } from 'primeng/tooltip';
import { Beneficiario } from 'src/app/domain/model/beneficiario/beneficiario';
import {
  HttpPolizaRepository
} from 'src/app/infrastructure/adapters/sin-oc/http-poliza.repository';
import {
  BeneficiarioRepository,
  BeneficiarioFiltroEstructurado
} from 'src/app/domain/service/beneficiario/beneficiario-repository';
import { CatalogoRepository } from 'src/app/domain/service/catalogo/catalogo-repository';
import {
  DEFAULT_ROWS_PER_PAGE,
  PAGE_SIZE_OPTIONS
} from 'src/app/common/constants-ejb';
import {
  onlyNumbersKeypress,
  sanitizeText,
  sanitizeTextPreserveSpaces
} from 'src/app/common/input-utils';
import { busquedaClientePreset } from 'src/app/presentation/shared/table-standardization/table-standard-presets';
import { BusquedaPaginadorComponent } from 'src/app/presentation/shared/busquedas/busqueda-paginador.component';
import { resolveSearchErrorMessage } from 'src/app/presentation/shared/busquedas/base-busqueda-paginada-dialog';

/**
 * Origen de datos del buscador de clientes.
 * Cada pantalla declara cuál usa para que los IDs retornados no se crucen entre fuentes.
 *
 * Legacy:
 *  BENEFICIARIO  → BuscadorBeneficiariosViewImpl (TPSC_BENEFICIARIO)
 *  CUERPOLIZA_SIN_OC → CumplimientoSinOcViewImpl (M094_CUERPOLIZA, selección por NPOLIZA)
 */
export enum BuscadorClienteOrigen {
  /** TPSC_BENEFICIARIO — filtro SNACTIVO='S' AND FEBAJA IS NULL. Valor por defecto. */
  BENEFICIARIO = 'BENEFICIARIO',
  /** M094_CUERPOLIZA — pólizas activas (FEBAJA > SYSDATE). Solo Sin Orientación de Servicio. */
  CUERPOLIZA_SIN_OC = 'CUERPOLIZA_SIN_OC',
}

/**
 * Filtros para búsqueda de empresas/beneficiarios.
 * Migrado del legacy: BuscadorBeneficiariosViewImpl.getDTO()
 *
 * Legacy behavior:
 * - tipoIdentificacion se incluye en la búsqueda SOLO si hay número de identificación
 * - Si tipo = NIT -> usa nombreComercial
 * - Si tipo = CC/otro -> usa nombre1, nombre2, apellido1, apellido2
 */
export interface FiltroCliente {
  contrato?: string;
  nombreCliente?: string;
  cdtipoIdentificacion?: string;
  numeroIdentificacion?: string;
  nombreComercial?: string;
  primerNombre?: string;
  segundoNombre?: string;
  primerApellido?: string;
  segundoApellido?: string;
}

/** Campos de texto libre del filtro (seguridad-girop §3). */
const CAMPOS_TEXTO_CLIENTE = [
  'contrato',
  'nombreCliente',
  'nombreComercial',
  'primerNombre',
  'segundoNombre',
  'primerApellido',
  'segundoApellido'
] as const;
type CampoTextoCliente = (typeof CAMPOS_TEXTO_CLIENTE)[number];

/**
 * Resultado de cliente seleccionado.
 * El campo `origen` indica la fuente de datos para que los consumidores
 * no crucen IDs de tablas distintas.
 *
 * BENEFICIARIO:       nmconsecutivo = TPSC_BENEFICIARIO.NMCONSECUTIVO
 *                     cdpersona     = TPSC_PERSONAS.CDPERSONA
 *                     contrato      = TPSC_BENEFICIARIO.CDCONTRATO
 *
 * CUERPOLIZA_SIN_OC:  contrato      = M094_CUERPOLIZA.NPOLIZA  ← campo clave
 *                     numeroIdentificacion = M094_CUERPOLIZA.DNI
 *                     nmconsecutivo / cdpersona NO aplican (serán 0)
 */
export interface ClienteSeleccionado {
  /** Fuente de datos. Ausente equivale a BENEFICIARIO (compatibilidad retroactiva). */
  origen?: BuscadorClienteOrigen;
  nmconsecutivo: number;
  cdpersona: number;
  tipoIdentificacion: string;
  descTipoIdentificacion?: string;
  numeroIdentificacion: string;
  /** CUERPOLIZA_SIN_OC: M094_CUERPOLIZA.DNI completo, sin reconstruir (legacy: persona.setDni) */
  dniCompleto?: string;
  nombreComercial: string;
  contrato?: string;
  /**
   * Solo CUERPOLIZA_SIN_OC: M094_CUERPOLIZA.CDDELEGACION (oficina principal de la póliza).
   * Legacy: CumplimientoSinOcViewImpl BG_RETURN_FIELD línea 617.
   */
  cddelegacion?: string;
}

/** Fila de la tabla: Beneficiario más la delegación de la póliza (solo CUERPOLIZA_SIN_OC). */
type FilaCliente = Beneficiario & { cddelegacion?: string };

/**
 * Diálogo de búsqueda de clientes/empresas.
 * Replica exactamente el legacy GWT: BuscadorBeneficiariosViewImpl
 *
 * Filtros:
 * - Contrato
 * - Tipo de Identificación (dropdown)
 * - Número de Identificación
 * - Primer Nombre, Segundo Nombre
 * - Primer Apellido, Segundo Apellido
 *
 * @author Ingeneo - Migración GWT a Angular
 */
@Component({
  selector: 'app-busqueda-cliente-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    DialogModule,
    TableModule,
    InputTextModule,
    ButtonModule,
    DropdownModule,
    ProgressSpinnerModule,
    TooltipModule,
    BusquedaPaginadorComponent
  ],
  styleUrls: ['../../shared/styles/grid-table.styles.scss'],
  template: `
    <p-dialog
      [header]="titulo() || 'Filtros Consulta de Empresas'"
      [(visible)]="visible"
      [modal]="true"
      [style]="{ width: '92vw', maxWidth: '52rem' }"
      [contentStyle]="{ 'max-height': '80vh', 'overflow-y': 'auto' }"
      [breakpoints]="{ '1024px': '88vw', '768px': '92vw', '640px': '96vw' }"
      [closable]="true"
      (onHide)="onCancelar()"
    >
      <!-- Sección Filtros de Búsqueda -->
      <div
        class="border dark:border-gray-600 rounded p-4 mb-4 bg-gray-50 dark:bg-gray-800"
      >
        <div class="font-medium text-gray-700 mb-3 text-sm">
          Filtros de Búsqueda
        </div>

        @if (soloContratoYNombre()) {
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
            <div>
              <label class="block text-sm text-gray-600 mb-1">Contrato</label>
              <input
                pInputText
                [(ngModel)]="filtro.contrato"
                (input)="sanitizarCampo('contrato')"
                class="w-full"
                maxlength="50"
              />
            </div>
            <div>
              <label class="block text-sm text-gray-600 mb-1"
                >Nombre del cliente</label
              >
              <input
                pInputText
                [(ngModel)]="filtro.nombreCliente"
                (input)="sanitizarCampo('nombreCliente')"
                class="w-full"
                maxlength="150"
              />
            </div>
          </div>
        } @else {
          <!-- Contrato -->
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
            <div>
              <label class="block text-sm text-gray-600 dark:text-gray-300 mb-1"
                >Contrato</label
              >
              <input
                pInputText
                [(ngModel)]="filtro.contrato"
                (input)="sanitizarCampo('contrato')"
                class="w-full"
                maxlength="50"
              />
            </div>
            <div></div>
          </div>

          <!-- Identificación: Tipo + Número (misma fila) -->
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
            <div>
              <label class="block text-sm text-gray-600 dark:text-gray-300 mb-1"
                >Identificación</label
              >
              <p-dropdown
                [(ngModel)]="filtro.cdtipoIdentificacion"
                [options]="tiposIdentificacion()"
                optionLabel="descripcion"
                optionValue="codigo"
                placeholder="Tipo"
                [filter]="true"
                filterBy="descripcion"
                [style]="{ width: '100%' }"
                appendTo="body"
                (onChange)="onTipoIdChange()"
              >
              </p-dropdown>
            </div>
            <div>
              <label class="block text-sm text-gray-600 dark:text-gray-300 mb-1"
                >Número</label
              >
              <input
                pInputText
                [(ngModel)]="filtro.numeroIdentificacion"
                class="w-full"
                maxlength="20"
                (keydown)="soloNumeros($event)"
              />
            </div>
          </div>

          <!-- Legacy: Si tipo=NIT muestra Nombre Comercial, sino muestra nombres persona natural -->
          @if (esNit()) {
            <!-- Nombre Comercial (persona jurídica / NIT) -->
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label
                  class="block text-sm text-gray-600 dark:text-gray-300 mb-1"
                  >Nombre Comercial</label
                >
                <input
                  pInputText
                  [(ngModel)]="filtro.nombreComercial"
                  (input)="sanitizarCampo('nombreComercial')"
                  class="w-full"
                  maxlength="150"
                />
              </div>
              <div></div>
            </div>
          } @else {
            <!-- Nombres persona natural (CC u otro tipo) -->
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label
                  class="block text-sm text-gray-600 dark:text-gray-300 mb-1"
                  >Primer Nombre</label
                >
                <input
                  pInputText
                  [(ngModel)]="filtro.primerNombre"
                  (input)="sanitizarCampo('primerNombre')"
                  class="w-full"
                  maxlength="50"
                />
              </div>
              <div>
                <label
                  class="block text-sm text-gray-600 dark:text-gray-300 mb-1"
                  >Segundo Nombre</label
                >
                <input
                  pInputText
                  [(ngModel)]="filtro.segundoNombre"
                  (input)="sanitizarCampo('segundoNombre')"
                  class="w-full"
                  maxlength="50"
                />
              </div>
              <div>
                <label
                  class="block text-sm text-gray-600 dark:text-gray-300 mb-1"
                  >Primer Apellido</label
                >
                <input
                  pInputText
                  [(ngModel)]="filtro.primerApellido"
                  (input)="sanitizarCampo('primerApellido')"
                  class="w-full"
                  maxlength="50"
                />
              </div>
              <div>
                <label
                  class="block text-sm text-gray-600 dark:text-gray-300 mb-1"
                  >Segundo Apellido</label
                >
                <input
                  pInputText
                  [(ngModel)]="filtro.segundoApellido"
                  (input)="sanitizarCampo('segundoApellido')"
                  class="w-full"
                  maxlength="50"
                />
              </div>
            </div>
          }
        }

        <!-- Botones de filtro -->
        <div
          class="flex flex-wrap gap-2 sm:gap-3 mt-4 justify-end sm:justify-start w-full"
        >
          <p-button
            label="Limpiar"
            icon="pi pi-eraser"
            severity="secondary"
            [outlined]="true"
            size="small"
            styleClass="min-h-[44px]"
            (onClick)="limpiarFiltros()"
          />
          <p-button
            label="Buscar"
            icon="pi pi-search"
            size="small"
            [loading]="loading()"
            styleClass="min-h-[44px]"
            (onClick)="buscar()"
          />
        </div>
      </div>

      <!-- Mensaje de estado -->
      @if (mensaje()) {
        <div
          class="p-3 mb-3 rounded"
          [ngClass]="{
            'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-200':
              mensajeTipo() === 'warning',
            'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200':
              mensajeTipo() === 'info',
            'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200':
              mensajeTipo() === 'error'
          }"
        >
          <i
            class="pi mr-2"
            [ngClass]="{
              'pi-exclamation-triangle': mensajeTipo() === 'warning',
              'pi-info-circle': mensajeTipo() === 'info',
              'pi-times-circle': mensajeTipo() === 'error'
            }"
          ></i>
          {{ mensaje() }}
        </div>
      }

      <!-- Loading -->
      @if (loading()) {
        <div class="flex justify-center p-4">
          <p-progressSpinner
            strokeWidth="4"
            [style]="{ width: '50px', height: '50px' }"
          ></p-progressSpinner>
        </div>
      }

      <!-- Listado de Empresas -->
      <div class="border dark:border-gray-600 rounded">
        <div
          class="font-medium text-gray-700 dark:text-gray-300 p-3 border-b dark:border-gray-600 bg-gray-50 dark:bg-gray-800 text-sm flex justify-between items-center"
        >
          <span>Listado de Empresas</span>
          @if (totalRegistros() > 0) {
            <span class="text-xs text-gray-500">
              Registros del {{ registroInicio() }} hasta el
              {{ registroFin() }} de {{ totalRegistros() }}
            </span>
          }
        </div>

        @if (!loading() && items().length > 0) {
          <div class="md:hidden space-y-3 p-3">
            @for (item of items(); track item.nmconsecutivo) {
              <div
                class="border border-gray-200 dark:border-gray-700 rounded-lg p-3 bg-white dark:bg-gray-900"
              >
                <div class="mb-3 min-w-0">
                  <div class="text-xs text-gray-500 dark:text-gray-400">
                    Nombre
                  </div>
                  <div
                    class="font-semibold text-gray-800 dark:text-gray-100 truncate"
                    [attr.title]="
                      tituloTabla(item.nombreComercial || item.nombreCompleto)
                    "
                  >
                    {{
                      textoTabla(item.nombreComercial || item.nombreCompleto)
                    }}
                  </div>
                </div>

                <div class="grid grid-cols-1 gap-2 text-sm mb-3">
                  <div>
                    <span class="text-gray-500 dark:text-gray-400"
                      >Tipo Identificación:</span
                    >
                    <span class="text-gray-800 dark:text-gray-200">{{
                      textoTabla(
                        item.descTipoIdentificacion || item.tipoIdentificacion
                      )
                    }}</span>
                  </div>
                  <div>
                    <span class="text-gray-500 dark:text-gray-400"
                      >Número Identificación:</span
                    >
                    <span class="text-gray-800 dark:text-gray-200">{{
                      textoTabla(item.numeroIdentificacion)
                    }}</span>
                  </div>
                  <div>
                    <span class="text-gray-500 dark:text-gray-400"
                      >Contrato:</span
                    >
                    <span class="text-gray-800 dark:text-gray-200">{{
                      textoTabla(item.cdcontrato)
                    }}</span>
                  </div>
                </div>

                <p-button
                  label="Seleccionar"
                  icon="pi pi-check"
                  size="small"
                  styleClass="w-full min-h-[44px]"
                  (onClick)="seleccionarItem(item)"
                />
              </div>
            }
          </div>

          <div class="hidden md:block">
            <div
              role="region"
              [attr.aria-label]="tableConfig().a11y.tableAriaLabel"
              class="sr-only"
            ></div>
            <p-table
              [resizableColumns]="true"
              columnResizeMode="expand"
              [value]="items()"
              [scrollable]="true"
              [responsiveLayout]="'scroll'"
              [scrollHeight]="tableConfig().responsive.scrollHeight || '250px'"
              [selectionMode]="tableSelectionMode()"
              [(selection)]="seleccionado"
              (onRowSelect)="onRowSelect($event)"
              [tableStyle]="{
                'min-width': tableConfig().responsive.minWidth || 'max-content'
              }"
              [dataKey]="
                (tableConfig().selection.dataKey || 'nmconsecutivo').toString()
              "
              [styleClass]="tableStyleClass()"
            >
              <ng-template pTemplate="header">
                <tr>
                  <th pResizableColumn>
                    <span class="grid-header-text" title="Tipo Identificación"
                      >Tipo Identificación</span
                    >
                  </th>
                  <th pResizableColumn>
                    <span
                      class="grid-header-text"
                      title="Número Identificación"
                      >Número Identificación</span
                    >
                  </th>
                  <th pResizableColumn>
                    <span class="grid-header-text" title="Nombre">Nombre</span>
                  </th>
                  <th pResizableColumn>
                    <span class="grid-header-text" title="Contrato"
                      >Contrato</span
                    >
                  </th>
                  <th pResizableColumn>
                    <span class="grid-header-text" title="Seleccionar"
                      >Seleccionar</span
                    >
                  </th>
                </tr>
              </ng-template>
              <ng-template pTemplate="body" let-item>
                <tr [pSelectableRow]="item">
                  <td>
                    <span
                      class="grid-cell-text"
                      [attr.title]="
                        tituloTabla(
                          item.descTipoIdentificacion || item.tipoIdentificacion
                        )
                      "
                      >{{
                        textoTabla(
                          item.descTipoIdentificacion || item.tipoIdentificacion
                        )
                      }}</span
                    >
                  </td>
                  <td>
                    <span
                      class="grid-cell-text"
                      [attr.title]="tituloTabla(item.numeroIdentificacion)"
                      >{{ textoTabla(item.numeroIdentificacion) }}</span
                    >
                  </td>
                  <td>
                    <span
                      class="grid-cell-text"
                      [attr.title]="
                        tituloTabla(item.nombreComercial || item.nombreCompleto)
                      "
                      >{{
                        textoTabla(item.nombreComercial || item.nombreCompleto)
                      }}</span
                    >
                  </td>
                  <td>
                    <span
                      class="grid-cell-text"
                      [attr.title]="tituloTabla(item.cdcontrato)"
                      >{{ textoTabla(item.cdcontrato) }}</span
                    >
                  </td>
                  <td>
                    <button
                      pButton
                      label="Seleccionar"
                      class="p-button-sm p-button-text"
                      (click)="seleccionarItem(item)"
                    ></button>
                  </td>
                </tr>
              </ng-template>
            </p-table>
          </div>

          <app-busqueda-paginador
            [currentPage]="paginaActual()"
            [totalPages]="totalPaginas()"
            [hasPrevious]="hasPrevious()"
            [hasNext]="hasNext()"
            [pageSizeOptions]="pageSizeOptions"
            [selectedPageSize]="selectedPageSize"
            (pageSizeChange)="selectedPageSize = $event; onPageSizeChange()"
            (first)="irPrimeraPagina()"
            (previous)="loadPreviousPage()"
            (next)="loadNextPage()"
            (last)="irUltimaPagina()"
          ></app-busqueda-paginador>
        }

        @if (!loading() && items().length === 0) {
          <div class="text-center text-gray-500 p-4">No hay datos</div>
        }
      </div>

      <!-- Botones footer -->
      <ng-template pTemplate="footer">
        <div
          class="flex flex-wrap justify-end sm:justify-start gap-2 sm:gap-3 w-full"
        >
          <p-button
            label="Cerrar"
            icon="pi pi-times"
            [outlined]="true"
            styleClass="min-h-[44px]"
            (onClick)="onCancelar()"
          ></p-button>
        </div>
      </ng-template>
    </p-dialog>
  `
})
export class BusquedaClienteDialogComponent implements OnInit {
  titulo = input<string>('');
  soloContratoYNombre = input<boolean>(false);
  /** Origen de datos — determina qué tabla/fuente consulta el buscador. */
  origen = input<BuscadorClienteOrigen>(BuscadorClienteOrigen.BENEFICIARIO);

  private readonly catalogoRepository = inject(CatalogoRepository);
  private readonly beneficiarioRepository = inject(BeneficiarioRepository);
  private readonly polizaRepository = inject(HttpPolizaRepository);

  // === Outputs ===
  onSelect = output<ClienteSeleccionado>();
  onClose = output<void>();

  /** Sanitización XSS/SQLi (seguridad-girop §3) para filtros de texto libre */
  sanitizeText = sanitizeText;

  // === State ===
  visible = true;
  filtro: FiltroCliente = {};
  seleccionado: Beneficiario | null = null;

  items = signal<FilaCliente[]>([]);
  loading = signal(false);
  mensaje = signal('');
  mensajeTipo = signal<'info' | 'warning' | 'error'>('info');
  tiposIdentificacion = signal<{ codigo: string; descripcion: string }[]>([]);

  // === Paginación (igual que personas-list.component.ts) ===
  // Ambos orígenes (BENEFICIARIO y CUERPOLIZA_SIN_OC) usan paginación
  // Keyset/Cursor: el backend sí calcula totalElements (contarConFiltros /
  // BuscarPolizaUseCase.contar), pero paginaActual es solo un contador local
  // de navegación, no un índice de página real.
  totalRegistros = signal(0);
  paginaActual = signal(1);
  pageSize = signal(DEFAULT_ROWS_PER_PAGE);

  // Opciones de tamaño de página (legacy: ConstantsEJB)
  pageSizeOptions = PAGE_SIZE_OPTIONS;
  selectedPageSize = DEFAULT_ROWS_PER_PAGE;

  // Computados para paginación
  totalPaginas = computed(() => {
    const total = this.totalRegistros();
    const size = this.pageSize();
    return total > 0 ? Math.ceil(total / size) : 0;
  });

  // Flags de navegación: los fija la RESPUESTA del backend (frontend-girop 17.7).
  hasNext = signal(false);
  hasPrevious = signal(false);

  registroInicio = computed(() => {
    if (this.totalRegistros() === 0) return 0;
    return (this.paginaActual() - 1) * this.pageSize() + 1;
  });

  registroFin = computed(() => {
    if (this.totalRegistros() === 0) return 0;
    const fin = this.registroInicio() + this.items().length - 1;
    return Math.min(fin, this.totalRegistros());
  });

  readonly tableConfig = computed(() =>
    busquedaClientePreset.createConfig({
      selection: {
        mode: 'single',
        dataKey: 'nmconsecutivo',
        selected: this.seleccionado as unknown as Record<string, unknown> | null
      },
      style: {
        tableClassName: 'tabla-busqueda-cliente',
        striped: true,
        gridlines: false,
        resizableColumns: true,
        rowHover: false
      },
      state: {
        loading: this.loading(),
        emptyMessage: 'No hay datos'
      },
      responsive: {
        mode: 'hybrid',
        minWidth: 'max-content',
        scrollHeight: '250px',
        cardBreakpointPx: 768
      },
      a11y: {
        tableAriaLabel: 'Tabla de busqueda de clientes',
        announceSelection: true,
        announcePageChanges: true,
        focusFirstActionOnRowSelect: false
      }
    })
  );

  readonly tableStyleClass = computed(() => {
    const config = this.tableConfig();
    const classes = ['p-datatable-sm', config.style.tableClassName];
    if (config.style.gridlines !== false) {
      classes.push('p-datatable-gridlines');
    }
    if (config.style.striped !== false) {
      classes.push('p-datatable-striped');
    }
    return classes.join(' ');
  });

  readonly tableSelectionMode = computed<'single' | 'multiple' | undefined>(
    () => {
      const mode = this.tableConfig().selection.mode;
      if (mode === 'single' || mode === 'multiple') {
        return mode;
      }
      return undefined;
    }
  );

  /**
   * Legacy: si tipo_identificacion = 'N' (NIT), muestra campo "Nombre Comercial"
   * sino muestra nombre1, nombre2, apellido1, apellido2
   * Constants.LIST_VALUE_TYPE_ID_NIT = "N"
   */
  esNit(): boolean {
    return this.filtro.cdtipoIdentificacion === 'N';
  }

  ngOnInit(): void {
    if (!this.soloContratoYNombre()) {
      this.cargarTiposIdentificacion();
    }
  }

  /**
   * Carga los tipos de identificación desde BD.
   * Tabla: TPSC_TIPOS_IDENTIFICACION
   * Legacy: ComunesFacade.listarTiposIdenti() con preselección CC
   */
  private async cargarTiposIdentificacion(): Promise<void> {
    try {
      const tipos = await this.catalogoRepository.getTiposIdentificacion();
      this.tiposIdentificacion.set(tipos);
      // Legacy: setDatosListas(Constants.LIST_VALUE_TYPE_ID_CC) -> preselecciona CC
      this.filtro.cdtipoIdentificacion = 'C';
    } catch (error) {
      console.error('Error cargando tipos de identificación:', error);
    }
  }

  /**
   * Legacy: addChangeHandlerIdPersona()
   * Cuando cambia el tipo de identificación, se limpian los campos de nombre
   * y se muestran/ocultan según sea NIT o persona natural.
   */
  onTipoIdChange(): void {
    // Limpiar campos de nombre al cambiar tipo (legacy: panelFiltros.clearElements() parcial)
    this.filtro.nombreComercial = '';
    this.filtro.primerNombre = '';
    this.filtro.segundoNombre = '';
    this.filtro.primerApellido = '';
    this.filtro.segundoApellido = '';
  }

  /**
   * Construye el objeto de filtros estructurados para enviar al backend.
   * Migrado de BuscadorBeneficiariosViewImpl.getDTO()
   *
   * Legacy logic:
   * - tipoIdentificacion se envía SOLO si hay número de identificación
   * - Si NIT -> envía nombreComercial
   * - Si CC/otro -> envía nombre1, nombre2, apellido1, apellido2
   * - contrato siempre se envía si tiene valor
   */
  private construirFiltrosEstructurados(): BeneficiarioFiltroEstructurado {
    const filtros: BeneficiarioFiltroEstructurado = {};

    // Contrato
    if (this.filtro.contrato?.trim()) {
      filtros.contrato = this.filtro.contrato.trim();
    }

    if (this.soloContratoYNombre()) {
      // Modo empresa simple: solo contrato + nombre comercial
      if (this.filtro.nombreCliente?.trim()) {
        filtros.nombreComercial = this.filtro.nombreCliente.trim();
      }
      return filtros;
    }

    // Legacy: tipo_id solo se incluye si hay número de identificación
    if (this.filtro.numeroIdentificacion?.trim()) {
      if (this.filtro.cdtipoIdentificacion) {
        filtros.tipoIdentificacion = this.filtro.cdtipoIdentificacion;
      }
      filtros.numeroIdentificacion = this.filtro.numeroIdentificacion.trim();
    }

    // Campos según tipo: NIT -> nombreComercial, CC/otro -> nombres persona natural
    if (this.esNit()) {
      if (this.filtro.nombreComercial?.trim()) {
        filtros.nombreComercial = this.filtro.nombreComercial.trim();
      }
    } else {
      if (this.filtro.primerNombre?.trim()) {
        filtros.nombre1 = this.filtro.primerNombre.trim();
      }
      if (this.filtro.segundoNombre?.trim()) {
        filtros.nombre2 = this.filtro.segundoNombre.trim();
      }
      if (this.filtro.primerApellido?.trim()) {
        filtros.apellido1 = this.filtro.primerApellido.trim();
      }
      if (this.filtro.segundoApellido?.trim()) {
        filtros.apellido2 = this.filtro.segundoApellido.trim();
      }
    }

    return filtros;
  }

  /**
   * Ejecuta la búsqueda con los filtros actuales.
   * Legacy: onClickBuscar -> setIniciado(true) -> stListaBeneficiarios.clear() -> onChange
   */
  async buscar(): Promise<void> {
    for (const campo of CAMPOS_TEXTO_CLIENTE) {
      this.sanitizarCampo(campo, true);
    }
    this.mensaje.set('');
    await this.cargarPagina(null, 'NEXT', 1);
  }

  /**
   * Sanitización XSS de texto libre (seguridad-girop §3). Mientras se teclea
   * conserva los espacios; antes de buscar también los recorta.
   */
  sanitizarCampo(campo: CampoTextoCliente, recortar = false): void {
    const valor = this.filtro[campo];
    if (valor === undefined) return;
    this.filtro[campo] = recortar
      ? sanitizeText(valor)
      : sanitizeTextPreserveSpaces(valor);
  }

  /** Despacha la carga según el origen; la página solo cambia tras la respuesta exitosa. */
  private async cargarPagina(
    cursor: string | null,
    direction: 'NEXT' | 'PREVIOUS' | 'LAST',
    pagina: number
  ): Promise<void> {
    if (this.origen() === BuscadorClienteOrigen.CUERPOLIZA_SIN_OC) {
      await this.buscarPaginaPoliza(cursor, direction, pagina);
      return;
    }
    await this.buscarPaginaBeneficiario(cursor, direction, pagina);
  }

  /** Aplica cursores, flags y número de página de una respuesta exitosa. */
  private aplicarPaginacion(
    response:
      | {
          totalElements?: number;
          nextCursor?: string | null;
          previousCursor?: string | null;
          hasNext?: boolean;
          hasPrevious?: boolean;
        }
      | null
      | undefined,
    direction: 'NEXT' | 'PREVIOUS' | 'LAST',
    pagina: number
  ): void {
    const total = response?.totalElements ?? 0;
    this.totalRegistros.set(total);
    this.nextCursorActual.set(response?.nextCursor ?? null);
    this.previousCursorActual.set(response?.previousCursor ?? null);
    this.hasNext.set(response?.hasNext ?? false);
    this.hasPrevious.set(response?.hasPrevious ?? false);
    if (direction === 'LAST') {
      const size = this.pageSize();
      this.paginaActual.set(
        size > 0 && total > 0 ? Math.max(1, Math.ceil(total / size)) : 1
      );
    } else {
      this.paginaActual.set(pagina);
    }
  }

  /** Limpia resultados ante un error; paginaActual no se modifica. */
  private limpiarPorError(): void {
    this.items.set([]);
    this.totalRegistros.set(0);
    this.hasNext.set(false);
    this.hasPrevious.set(false);
    this.nextCursorActual.set(null);
    this.previousCursorActual.set(null);
  }

  /** Cursor devuelto por el backend para pedir la página siguiente (origen BENEFICIARIO/CUERPOLIZA_SIN_OC). */
  private nextCursorActual = signal<string | null>(null);

  /** Cursor devuelto por el backend para pedir la página anterior (origen BENEFICIARIO/CUERPOLIZA_SIN_OC). */
  private previousCursorActual = signal<string | null>(null);

  /**
   * Ejecuta la búsqueda en el backend con filtros estructurados usando
   * paginación keyset/cursor (skill acceso-datos-oracle §7 — nunca OFFSET).
   */
  private async buscarPaginaBeneficiario(
    cursor: string | null,
    direction: 'NEXT' | 'PREVIOUS' | 'LAST',
    pagina = 1
  ): Promise<void> {
    this.loading.set(true);
    this.mensaje.set('');

    try {
      const filtros = this.construirFiltrosEstructurados();
      const size = this.pageSize();

      const response =
        await this.beneficiarioRepository.buscarConFiltrosEstructurados(
          filtros,
          cursor,
          size,
          direction
        );

      const content = response?.content ?? [];
      this.items.set(content);
      // paginaActual solo alimenta las etiquetas y cambia únicamente tras la respuesta.
      this.aplicarPaginacion(response, direction, pagina);

      if (content.length === 0) {
        this.mensaje.set(
          'No se recuperó información con los parámetros ingresados.'
        );
        this.mensajeTipo.set('warning');
      }
    } catch (error) {
      console.error('Error en búsqueda:', error);
      this.mensaje.set(
        resolveSearchErrorMessage(error, 'Error al realizar la búsqueda')
      );
      this.mensajeTipo.set('error');
      this.limpiarPorError();
    } finally {
      this.loading.set(false);
    }
  }

  /**
   * Búsqueda en M094_CUERPOLIZA para Sin Orientación de Servicio, con
   * paginación keyset/cursor (skill acceso-datos-oracle §7 — nunca OFFSET).
   * Mapea Poliza al shape de Beneficiario para reutilizar la tabla existente:
   *   cdcontrato       = NPOLIZA  (campo clave que usa el consumer)
   *   numeroIdentificacion = DNI
   *   nombreComercial  = DSCOMERCIAL
   *   nmconsecutivo / cdpersona = 0 (no aplica para pólizas)
   */
  private async buscarPaginaPoliza(
    cursor: string | null,
    direction: 'NEXT' | 'PREVIOUS' | 'LAST',
    pagina = 1
  ): Promise<void> {
    this.loading.set(true);
    this.mensaje.set('');

    try {
      const size = this.pageSize();
      const response = await this.polizaRepository.buscar(
        this.filtro.contrato,
        this.filtro.nombreCliente,
        cursor,
        size,
        direction
      );

      const content = (response?.content ?? []).map((p, index) => ({
        nmconsecutivo: index + 1,             // único por página — evita que PrimeNG marque todas las filas
        cdpersona: p.cdpersona ?? 0,
        tipoIdentificacion: p.cdtipoIdentificacion ?? '',
        descTipoIdentificacion: p.dstipoIdentificacion ?? '',
        numeroIdentificacion: p.dni ?? '',   // parte numérica, sin prefijo de tipo
        dniCompleto: p.dniCompleto ?? undefined,
        nombreComercial: p.dscomercial ?? '',
        nombreCompleto: '',
        cdcontrato: p.npoliza ?? '',          // NPOLIZA → campo clave para el SP
        snactivo: 'S',
        cddelegacion: p.cddelegacion ?? undefined
      }));

      this.items.set(content);
      // paginaActual solo alimenta las etiquetas y cambia únicamente tras la respuesta.
      this.aplicarPaginacion(response, direction, pagina);

      if (content.length === 0) {
        this.mensaje.set('No se recuperó información con los parámetros ingresados.');
        this.mensajeTipo.set('warning');
      }
    } catch (error) {
      console.error('Error buscando pólizas:', error);
      this.mensaje.set(
        resolveSearchErrorMessage(error, 'Error al realizar la búsqueda')
      );
      this.mensajeTipo.set('error');
      this.limpiarPorError();
    } finally {
      this.loading.set(false);
    }
  }

  /**
   * Limpia todos los filtros.
   * Legacy: onClickLimpiar -> panelFiltros.clearElements() + mostrarOcultarCampos
   */
  limpiarFiltros(): void {
    if (this.soloContratoYNombre()) {
      this.filtro = {};
    } else {
      // Legacy: reset() -> lbTipoIdPersona back to CC, clear fields
      this.filtro = { cdtipoIdentificacion: 'C' };
    }
    this.items.set([]);
    this.mensaje.set('');
    this.totalRegistros.set(0);
    this.paginaActual.set(1);
    this.hasNext.set(false);
    this.hasPrevious.set(false);
    this.nextCursorActual.set(null);
    this.previousCursorActual.set(null);
    this.hasNext.set(false);
    this.hasPrevious.set(false);
  }

  /**
   * Cambia el tamaño de página y recarga.
   */
  async onPageSizeChange(): Promise<void> {
    this.pageSize.set(this.selectedPageSize);
    await this.cargarPagina(null, 'NEXT', 1);
  }

  /**
   * Va a la primera página.
   */
  async irPrimeraPagina(): Promise<void> {
    if (this.loading()) return;
    await this.cargarPagina(null, 'NEXT', 1);
  }

  /**
   * Va a la página anterior.
   */
  async loadPreviousPage(): Promise<void> {
    if (this.loading() || !this.hasPrevious()) return;
    await this.cargarPagina(
      this.previousCursorActual(),
      'PREVIOUS',
      Math.max(1, this.paginaActual() - 1)
    );
  }

  /**
   * Va a la página siguiente.
   */
  async loadNextPage(): Promise<void> {
    if (this.loading() || !this.hasNext()) return;
    await this.cargarPagina(
      this.nextCursorActual(),
      'NEXT',
      this.paginaActual() + 1
    );
  }

  /**
   * Va a la última página (cursor nulo + LAST; la página se fija con la respuesta).
   */
  async irUltimaPagina(): Promise<void> {
    if (this.loading() || this.totalRegistros() <= 0) return;
    await this.cargarPagina(
      null,
      'LAST',
      Math.max(1, Math.ceil(this.totalRegistros() / this.pageSize()))
    );
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onRowSelect(event: any): void {
    if (event.data) {
      this.seleccionado = event.data;
    }
  }

  seleccionarItem(item: FilaCliente): void {
    // Para CUERPOLIZA_SIN_OC: cdcontrato=NPOLIZA, numeroIdentificacion=DNI; nmconsecutivo/cdpersona=0
    const resultado: ClienteSeleccionado = {
      origen: this.origen(),
      nmconsecutivo: item.nmconsecutivo,
      cdpersona: item.cdpersona,
      tipoIdentificacion: item.tipoIdentificacion,
      numeroIdentificacion: item.numeroIdentificacion,
      dniCompleto: item.dniCompleto,
      nombreComercial: item.nombreComercial || item.nombreCompleto || '',
      contrato: item.cdcontrato,
      ...(item.cddelegacion ? { cddelegacion: item.cddelegacion } : {})
    };
    this.onSelect.emit(resultado);
    this.visible = false;
  }

  onCancelar(): void {
    this.visible = false;
    this.onClose.emit();
  }

  textoTabla(valor: unknown): string {
    const texto = String(valor ?? '').trim();
    return texto || '-';
  }

  tituloTabla(valor: unknown): string {
    return String(valor ?? '').trim();
  }

  /**
   * Legacy: SuraNumericField con REGEX_JUST_NUMBERS
   * Wrapper de onlyNumbersKeypress para uso en template.
   */
  soloNumeros(event: KeyboardEvent): void {
    if (!onlyNumbersKeypress(event)) {
      event.preventDefault();
    }
  }
}
